/**
 * Site form validation utilities
 */

export interface SiteFormData {
  name: string;
  domain: string;
}

export interface SiteValidationErrors {
  name?: string;
  domain?: string;
}

/**
 * Normalize a domain input:
 * - Strip protocol (http://, https://)
 * - Strip paths and query strings
 * - Convert to lowercase
 */
export function normalizeDomain(input: string): string {
  return input
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\?.*$/, "")
    .toLowerCase()
    .trim();
}

/**
 * Validate a hostname format
 * Allows subdomains, TLDs, and valid hostname characters
 */
function isValidHostname(domain: string): boolean {
  // Domain must have at least one dot (e.g., example.com)
  if (!domain.includes(".")) {
    return false;
  }

  // Valid hostname pattern:
  // - Starts with alphanumeric
  // - Can contain alphanumeric and hyphens
  // - Labels separated by dots
  // - Each label must start and end with alphanumeric
  const hostnamePattern =
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
  return hostnamePattern.test(domain);
}

/**
 * Validate site form data
 * Returns errors object with field-specific error messages
 */
export function validateSiteForm(data: SiteFormData): SiteValidationErrors {
  const errors: SiteValidationErrors = {};

  // Validate name
  const trimmedName = data.name.trim();
  if (!trimmedName) {
    errors.name = "Site name is required";
  } else if (trimmedName.length > 100) {
    errors.name = "Site name must be 100 characters or less";
  }

  // Validate domain
  const normalizedDomain = normalizeDomain(data.domain);
  if (!normalizedDomain) {
    errors.domain = "Domain is required";
  } else if (!isValidHostname(normalizedDomain)) {
    errors.domain = "Please enter a valid domain (e.g., example.com)";
  }

  return errors;
}

/**
 * Check if validation errors object has any errors
 */
export function hasErrors(errors: SiteValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}
